<template>
  <div>
    <card-toolbar
      id="replication_title"
      icon="dns"
      :title="$tc('replication')"
    >
      <template #actions>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon @click="loadNodes">
                autorenew
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('refresh') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon @click="replicationPulse">
                wifi_tethering
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('pulse') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              icon
              @click="showCrossCheckModal = true"
              v-on="on"
            >
              <v-icon>compare_arrows</v-icon>
            </v-btn>
          </template>
          <span>{{ $tc('cross_check') }}</span>
        </v-tooltip>
      </template>
    </card-toolbar>

    <v-card flat class="rounded-lg mb-8" outlined style="background: transparent;">
      <v-row class="pa-4" wrap>
        <v-col v-if="info && info.repl_domain" cols="12" md="6">
          <strong>{{ $tc('domain') }}</strong>
          <p>
            {{ info.repl_domain }}
          </p>
        </v-col>
        <v-col v-if="info && info.sync_point" cols="12" md="6">
          <strong>{{ $tc('sync_point') }}</strong>
          <p>
            {{ info.sync_point }} {{ info.SPVV }}
          </p>
        </v-col>
        <v-col v-if="info && info.manager_pulse" cols="12" md="6">
          <strong>{{ $tc('manager_pulsing') }}</strong>
          <p>
            {{ info.manager_pulse }} {{ $tc('seconds') }}
          </p>
        </v-col>
        <v-col v-if="info && info.recovery_pulse" cols="12" md="6">
          <strong>{{ $tc('recovery_pulsing') }}</strong>
          <p>
            {{ info.recovery_pulse }} {{ $tc('seconds') }}
          </p>
        </v-col>
      </v-row>

      <v-card flat style="background: transparent;">
        <v-toolbar flat dense outlined style="background: transparent;">
          <v-icon class="ml-2 mr-4">
            device_hub
          </v-icon>

          <v-toolbar-title>{{ $tc('node', 2) }}</v-toolbar-title>

          <v-spacer />

          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn icon v-on="on" @click="createNode">
                <v-icon>add</v-icon>
              </v-btn>
            </template>
            <span>{{ $tc('add_node') }}</span>
          </v-tooltip>
        </v-toolbar>
        <v-data-table
          style="background: transparent;"
          :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
          hide-default-footer
          :headers="headers"
          :items="nodes"
          :loading="loading"
        >
          <template
            v-if="nodes.length"
            #body="{ items }"
          >
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td>{{ item.addr }}</td>
                <td>
                  <v-row no-gutters justify="end">
                    <table-item-loading v-if="item.probing" />
                    <v-tooltip v-else bottom>
                      <template #activator="{ on }">
                        <v-icon
                          v-on="on"
                          class="mr-2"
                          @click="testNode(item)"
                        >
                          settings_remote
                        </v-icon>
                      </template>
                      <span>{{ $tc('test_node') }}</span>
                    </v-tooltip>

                    <table-item-loading v-if="item.downing" />
                    <v-tooltip v-else bottom>
                      <template #activator="{ on }">
                        <v-icon
                          v-on="on"
                          class="mr-2"
                          @click="notifyNodeDown(item)"
                        >
                          thumb_down
                        </v-icon>
                      </template>
                      <span>{{ $tc('notify_node_down') }}</span>
                    </v-tooltip>

                    <table-item-loading v-if="item.deleting" />
                    <v-tooltip v-else bottom>
                      <template #activator="{ on }">
                        <v-icon
                          v-on="on"
                          class="mr-2"
                          @click="deleteNode(item)"
                        >
                          delete
                        </v-icon>
                      </template>
                      <span>{{ $tc('delete_node') }}</span>
                    </v-tooltip>
                  </v-row>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-card>
    </v-card>

    <node-new-modal
      :open="showCreateNodeModal"
      @close="showCreateNodeModal = false"
      @error="handleErrorCreatingNode"
      @success="nodeCreatedWithSuccess"
    />

    <cross-check-modal
      :open="showCrossCheckModal"
      @close="showCrossCheckModal = false"
    />
  </div>
</template>

<script>
import CardToolbar from '@/components/CardToolbar'
import TableItemLoading from '@/components/TableItemLoading'
import NodeNewModal from './modals/NodeNewModal'
import CrossCheckModal from './modals/CrossCheckModal'

const RNM_PROBE = 1
const RNM_DOWN = 2
const RNM_DEL = 4

export default {
  components: {
    NodeNewModal,
    CrossCheckModal,
    TableItemLoading,
    CardToolbar
  },
  created () {
    if (this.$store.state.auth.isLogged) {
      this.loadNodes()
    }
  },
  computed: {
    info () {
      return this.$store.state.hsm.statInfo
    },
    nodes () {
      if (!this.info || !this.info.nodes.length) {
        return []
      }
      return this.info.nodes.map(node => {
        return {
          addr: node,
          probing: false,
          downing: false,
          deleting: false
        }
      })
    },
    headers () {
      const node = this.$tc('node', 2)
      const action = this.$tc('action', 2)

      return [
        {
          text: node,
          align: 'left',
          sortable: true,
          value: 'addr'
        },
        {
          text: action,
          align: 'right',
          sortable: false,
          value: 'actions'
        }
      ]
    }
  },
  data () {
    return {
      showCrossCheckModal: false,
      showCreateNodeModal: false,
      showPowerOffNodeModal: false,
      selectedNode: null,
      itemsPerPageOptions: [10, 20, 30, -1],
      loading: false
    }
  },
  methods: {
    loadNodes () {
      this.loading = true
      this.$store
        .dispatch('hsm/getStatInfo')
        .then(() => {
          this.loading = false
          // console.log('INFO==>', this.info.nodes)
        })
        .catch(err => {
          throw err
        })
    },
    replicationPulse () {
      this.$store
        .dispatch('hsm/replPulse')
        .then(res => {
          // console.log('RES==>', res)
          return this.$notify.success({})
        })
        .catch(err => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })
    },
    createNode () {
      this.showCreateNodeModal = true
    },
    handleErrorCreatingNode (err) {
      if (err) {
        const errObj = this.$store.getters['error/getErrorObj'](err)
        if (errObj) {
          this.$notify.hsmError(errObj, err)
        } else {
          this.$notify.error({ message: err })
        }
      }
      this.showCreateNodeModal = false
      this.loadNodes()
    },
    nodeCreatedWithSuccess () {
      this.showCreateNodeModal = false
      this.loadNodes()
      this.$notify.success({})
    },
    command (operation, item) {
      let buttonUsed
      switch (operation) {
        case RNM_PROBE:
          buttonUsed = 'probing'
          break
        case RNM_DOWN:
          buttonUsed = 'downing'
          break
        case RNM_DEL:
          buttonUsed = 'deleting'
          break
        default:
          buttonUsed = 'probing'
          break
      }
      item[buttonUsed] = true
      this.$store
        .dispatch('hsm/replNodeMsg', { body: { op: operation, peer: item.addr } })
        .then(() => {
          this.loadNodes()
          this.$notify.success({})
        })
        .catch(err => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })
        .finally(() => {
          item[buttonUsed] = false
        })
    },
    confirm (op, item, msgTitle, msgText) {
      return this.$notify.confirm({ title: msgTitle, message: msgText }, () => {
        return this.command(op, item)
      })
    },
    notifyNodeDown (item) {
      this.confirm(RNM_DOWN, item, this.$t('notify_node_down'), this.$t('notify_node_down_message', [item.addr]))
    },
    deleteNode (item) {
      this.confirm(RNM_DEL, item, this.$t('delete_node'), this.$t('delete_node_message', [item.addr]))
    },
    testNode (item) {
      this.confirm(RNM_PROBE, item, this.$t('test_node'), this.$t('probe_node_message', [item.addr]))
    }
  }
}
</script>
